import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';

import { useSendOTP, useValidateOTP } from '@/modules/auth/apiHooks';
import { useLoginFunctions } from '@/modules/auth/components/FormsContainer/Login/logic';
import Header from '@/modules/auth/components/Header';
import { IAuth } from '@/modules/auth/types';
import { Button, Typography } from '@/shared/components/atoms';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';

const LoginOTP = () => {
  const { login } = useAuth();
  const { push, query } = useRouter();
  const { identifier, country } = query;
  const { t } = useTranslation('login');
  const [countdown, setCountdown] = useState(0);
  const { OTPValidationSchema } = useLoginFunctions();
  const { isLoading: isSendOTPLoading, refetch: refetchSendOTP } = useSendOTP({
    phoneNumber: identifier?.toString(),
    country: country?.toString() ?? 'EG',
  });
  const { mutate: validateOTP, isLoading: isValidateOTPLoading } =
    useValidateOTP();

  const formMethods = useForm({
    mode: 'all',
    defaultValues: {
      phoneNumber: identifier,
      code: '',
      country,
    } as IAuth.IValidateOTPSchema,
    resolver: yupResolver(OTPValidationSchema),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = formMethods;
  const codeWatcher = watch('code');

  const sendOTPHandler = () => {
    if (identifier === '' || !country) return;
    setCountdown(15);
    refetchSendOTP();
  };

  const onSubmit = (data: IAuth.IValidateOTPSchema) => {
    validateOTP(data, {
      onSuccess: (response) => {
        login(response.data.token, undefined, 1727202181651);
        push(routes.createPassword);
      },
    });
  };

  useEffect(() => {
    sendOTPHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, country]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prevCountdown) =>
        prevCountdown > 0 ? prevCountdown - 1 : 0
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-16'>
        <Header
          title={t('verify-phone-number')}
          subtitle={
            <div>
              <span className='text-base font-regular text-surface-10'>
                {t('verification-code-sent')}
              </span>{' '}
              <span className='text-base font-regular text-surface-10'>
                {identifier}
              </span>{' '}
              <span
                className='cursor-pointer text-base font-semi-bold capitalize text-primary-default'
                onClick={() => {
                  push(routes.login);
                }}
              >
                {t('change-phone-number')}
              </span>
            </div>
          }
        />

        <div className='flex flex-col gap-24'>
          <div>
            <Controller
              name='code'
              control={control}
              render={({ field }) => (
                <OtpInput
                  inputStyle='otp-input'
                  containerStyle='otp-container'
                  isInputNum
                  numInputs={4}
                  {...field}
                  aria-invalid={errors?.code ? 'true' : 'false'}
                />
              )}
            />
          </div>
          {countdown > 0 ? (
            <Typography
              variant='caption'
              weight='medium'
              className='w-fit rounded-xl bg-outline-default px-16 py-2 text-primary-default'
            >
              {t('you-can-resubmit-the-code-within')}{' '}
              <span className='font-regular text-text-default'>
                0:{countdown}
              </span>
            </Typography>
          ) : (
            <Typography
              variant='caption'
              className='w-fit cursor-pointer rounded-xl bg-outline-default px-16 py-2 text-primary-default'
              onClick={sendOTPHandler}
            >
              {t('Resend_the _code')}
            </Typography>
          )}
        </div>
        <Button
          size='full'
          type='submit'
          className='mt-32 w-full !rounded-xl py-14 text-base font-semi-bold'
          disabled={
            isValidateOTPLoading ||
            isSendOTPLoading ||
            identifier === '' ||
            country === '' ||
            codeWatcher.length !== 4
          }
        >
          {t('creat-password')}
        </Button>
      </form>
    </FormProvider>
  );
};

export default LoginOTP;
